// src/components/clients/autosave.ts
// Trailing-debounce autosave controller. Pure (no React deps) so it can be unit-tested.
//
// Usage from React: hold a single instance per editor in a ref. Call schedule(value)
// from onChange, flush() on blur/route-change/beforeunload, cancel() on errors you don't
// want to retry. The instance dedupes by last-saved value so a no-op schedule is free.

export interface Autosaver {
  schedule(value: string): void;
  flush(): Promise<void>;
  cancel(): void;
  hasPending(): boolean;
}

export function createAutosaver(
  save: (value: string) => Promise<void>,
  delayMs: number,
): Autosaver {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingValue: string | null = null;
  let lastSavedValue: string | null = null;
  let hasEverSaved = false;
  let activeSave: Promise<void> | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const runSave = async (value: string) => {
    // Commit dedupe state only after the server confirms the write. Saves run
    // serially, so an older request can never complete after a newer one and
    // overwrite it.
    try {
      await save(value);
      lastSavedValue = value;
      hasEverSaved = true;
    } catch (error) {
      throw error;
    }
  };

  const drain = (): Promise<void> => {
    if (activeSave) return activeSave;

    const task = (async () => {
      while (pendingValue !== null) {
        const value = pendingValue;
        pendingValue = null;
        if (hasEverSaved && value === lastSavedValue) continue;
        await runSave(value);
      }
    })();

    activeSave = task;
    task.then(
      () => {
        if (activeSave === task) activeSave = null;
      },
      () => {
        if (activeSave === task) activeSave = null;
      },
    );
    return task;
  };

  return {
    schedule(value: string) {
      if (hasEverSaved && value === lastSavedValue && !activeSave) {
        // The user returned to the confirmed server value before the debounce
        // fired, so discard any newer pending draft.
        pendingValue = null;
        clearTimer();
        return;
      }
      pendingValue = value;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        // Swallow errors from the timer-triggered path: the `save` callback is
        // responsible for logging/toasting. Rejections must not escape.
        void drain().catch(() => {
          /* handled by save callback */
        });
      }, delayMs);
    },
    async flush() {
      clearTimer();
      // Await both an in-flight write and the latest value queued behind it.
      // The loop also covers a value scheduled while the active request was
      // settling, without ever running two writes concurrently.
      while (activeSave || pendingValue !== null) {
        await drain();
        clearTimer();
      }
    },
    cancel() {
      clearTimer();
      pendingValue = null;
    },
    hasPending() {
      return pendingValue !== null || activeSave !== null;
    },
  };
}
